import { useCallback, useRef, useState } from 'react';
import { ModalOverlay } from '../components/ModalOverlay';
import { useModalA11y } from '../components/useModalA11y';
import {
  ArrowPathIcon,
  ArrowPathRoundedSquareIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { RotateDirection } from '@xphoto/shared';
import { ApiError } from './api';
import { useI18n } from '../i18n';
import { interpolate } from './format';
import { enqueueRotate } from '../organize/rotate-api';

/**
 * Modale de ROTATION de la sélection (outil « Pivoter »).
 *
 * Pivote les IMAGES en écrivant SEULEMENT le tag EXIF Orientation des originaux (pixels
 * intacts → identity_hash inchangé) ; les vidéos sont ignorées. La confirmation se fait en
 * cliquant l'un des trois SENS (droite 90° / gauche 90° / 180°). L'option « Simulation »
 * (dry-run) compte les cibles sans rien écrire.
 *
 * Boîte de dialogue accessible : `role="dialog"` + `aria-modal`, focus initial/restauré,
 * verrou de défilement, Échap ferme. Le caller l'enveloppe dans un `<Portal>`.
 */
interface RotateModalProps {
  libraryId: string;
  /** Ids des assets sélectionnés (capturés à l'ouverture). */
  assetIds: string[];
  onClose: () => void;
  /** Appelé après un enfilement réussi (p.ex. vider la sélection + rafraîchir). */
  onRotateQueued?: () => void;
}

/** Les trois sens proposés (icône + clé i18n du libellé). */
const DIRECTIONS: {
  direction: RotateDirection;
  labelKey: 'rotate.cw' | 'rotate.ccw' | 'rotate.flip';
  Icon: typeof ArrowUturnRightIcon;
}[] = [
  { direction: 'ccw', labelKey: 'rotate.ccw', Icon: ArrowUturnLeftIcon },
  { direction: 'cw', labelKey: 'rotate.cw', Icon: ArrowUturnRightIcon },
  { direction: 'flip', labelKey: 'rotate.flip', Icon: ArrowPathIcon },
];

export function RotateModal({
  libraryId,
  assetIds,
  onClose,
  onRotateQueued,
}: RotateModalProps): React.JSX.Element {
  const { t, locale } = useI18n();
  const firstButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const [dryRun, setDryRun] = useState(false);
  /** Sens en cours d'enfilement (désactive les boutons), ou `null`. */
  const [busy, setBusy] = useState<RotateDirection | null>(null);
  useModalA11y({
    dialogRef,
    initialFocusRef: firstButtonRef,
    onEscape: busy === null ? onClose : undefined,
  });
  const [error, setError] = useState<'writeDisabled' | 'generic' | null>(null);
  const [done, setDone] = useState(false);

  // Verrou de défilement + focus initial + restauration.

  // Échap ferme (sauf pendant l'appel réseau).

  const submit = useCallback(
    async (direction: RotateDirection) => {
      if (busy !== null) return;
      setBusy(direction);
      setError(null);
      try {
        await enqueueRotate(libraryId, { assetIds, direction, dryRun });
        setDone(true);
        onRotateQueued?.();
      } catch (err) {
        if (err instanceof ApiError && err.status === 409 && err.code === 'WRITE_DISABLED') {
          setError('writeDisabled');
        } else {
          setError('generic');
        }
      } finally {
        setBusy(null);
      }
    },
    [busy, libraryId, assetIds, dryRun, onRotateQueued],
  );

  return (
    <ModalOverlay onDismiss={busy === null ? onClose : undefined}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('rotate.title')}
        className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl"
      >
        {/* En-tête. */}
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
            <ArrowPathRoundedSquareIcon className="h-6 w-6 text-brand" aria-hidden="true" />
            {t('rotate.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy !== null}
            title={t('rotate.close')}
            aria-label={t('rotate.close')}
            className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* Corps. */}
        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto p-4">
          {done ? (
            <p
              role="status"
              className="flex items-start gap-2 rounded-md bg-surface-2 p-3 text-sm text-fg"
            >
              <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
              {t('rotate.queued')}
            </p>
          ) : (
            <>
              <p className="text-sm text-muted">
                {interpolate(t('rotate.intro'), { count: assetIds.length }, locale)}
              </p>
              <p className="flex items-start gap-2 rounded-md bg-surface-2 p-3 text-sm text-muted">
                <InformationCircleIcon
                  className="mt-0.5 h-5 w-5 shrink-0 text-brand"
                  aria-hidden="true"
                />
                {t('rotate.exifNote')}
              </p>

              {/* Choix du sens : cliquer un bouton confirme et enfile la rotation. */}
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {DIRECTIONS.map(({ direction, labelKey, Icon }, i) => (
                  <button
                    key={direction}
                    type="button"
                    ref={i === 0 ? firstButtonRef : undefined}
                    onClick={() => void submit(direction)}
                    disabled={busy !== null}
                    className="flex flex-col items-center gap-2 rounded-md border border-border p-4 text-sm font-medium text-fg transition-colors hover:border-brand hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
                  >
                    {busy === direction ? (
                      <ArrowPathIcon
                        className="h-7 w-7 animate-spin text-brand motion-reduce:animate-none"
                        aria-hidden="true"
                      />
                    ) : (
                      <Icon className="h-7 w-7 text-brand" aria-hidden="true" />
                    )}
                    <span className="text-center">{t(labelKey)}</span>
                  </button>
                ))}
              </div>

              <label className="flex items-start gap-2 text-sm text-fg">
                <input
                  type="checkbox"
                  checked={dryRun}
                  onChange={(e) => setDryRun(e.target.checked)}
                  disabled={busy !== null}
                  className="mt-0.5 h-4 w-4"
                />
                <span>{t('rotate.dryRun')}</span>
              </label>

              {error !== null && (
                <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
                  <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                  {error === 'writeDisabled' ? t('rotate.errorWriteDisabled') : t('rotate.error')}
                </p>
              )}
            </>
          )}
        </div>

        {/* Actions. */}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border p-4">
          {done ? (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              {t('rotate.close')}
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              disabled={busy !== null}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
            >
              {t('rotate.cancel')}
            </button>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}
